import Phaser from 'phaser';
import Player from '../objects/Player';
import Bullet from '../objects/Bullet';
import Enemy from '../objects/Enemy';
import EnemyBullet from '../objects/EnemyBullet';
import Door, { DoorDirection } from '../objects/Door';
import Chest from '../objects/Chest';
import Potion from '../objects/Potion';
import Coin from '../objects/Coin';
import {
  HIGH_SCORE_KEY,
  HIGH_FLOOR_KEY,
  loadNumber,
  saveNumber,
} from '../storage';
import { clampStartFloor, markFloorCleared } from '../progress';
import * as Bal from '../balance';
import { tr } from '../i18n';
import { BOSS_ENEMY } from '../enemies';
import {
  scoreMultiplier,
  coinDropCount,
  coinValue,
  KILL_BASE_SCORE,
  CHEST_SCORE,
  CHEST_HEAL,
  CHEST_COINS,
  POTION_HEAL,
  FLOOR_CLEAR_SCORE,
} from '../rewards';
import DungeonBuilder from '../dungeon/DungeonBuilder';
import EnemyDirector from '../dungeon/EnemyDirector';
import Hud from '../ui/Hud';
import BossBar from '../ui/BossBar';
import Toasts from '../ui/Toasts';
import TalentPanel from '../ui/TalentPanel';
import PauseMenu from '../ui/PauseMenu';
import { DEPTH, TINT, UI } from '../ui/theme';

/** 死亡后切到结算画面的延迟（ms，delta 累积） */
const DEATH_TRANSITION_MS = 600;
/** 换层后忽略门的冷却（ms） */
const DOOR_COOLDOWN_MS = 600;
/** 视野休眠的外扩范围（px） */
const VIEW_PAD_X = 240;
const VIEW_PAD_Y = 200;

/**
 * GameScene —— 只做「编排」：
 * 持有游戏状态、物理组、输入，并把职责委托给各模块
 * （DungeonBuilder / EnemyDirector / Hud / Toasts / TalentPanel / PauseMenu）。
 * 所有调参来自 balance.ts / rewards.ts / enemies.ts / talents.ts 的单一来源。
 */
export default class GameScene extends Phaser.Scene {
  player!: Player;
  bullets!: Phaser.Physics.Arcade.Group;
  enemies!: Phaser.Physics.Arcade.Group;
  enemyBullets!: Phaser.Physics.Arcade.Group;
  walls!: Phaser.Physics.Arcade.StaticGroup;
  doors!: Phaser.Physics.Arcade.StaticGroup;
  chests!: Phaser.Physics.Arcade.StaticGroup;
  potions!: Phaser.Physics.Arcade.Group;
  coins!: Phaser.Physics.Arcade.Group;

  score = 0;
  floor = 1;
  autoFire = false;
  paused = false;

  /** 起始楼层（默认第 1 层；从选关界面进入时由 init(data) 指定） */
  private startFloorOverride = 1;

  private fireTimer = 0;
  private doorCooldown = 0;
  private doorUnlocked = false;
  private highScore = 0;
  private highFloor = 0;
  private gameOver = false;
  private deathTimer = 0;
  private deathTransitioned = false;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: any;
  private spaceKey!: Phaser.Input.Keyboard.Key;

  private builder!: DungeonBuilder;
  private director!: EnemyDirector;
  private hud!: Hud;
  private bossBar!: BossBar;
  private toasts!: Toasts;
  private talents!: TalentPanel;
  private pause!: PauseMenu;

  constructor() {
    super({ key: 'GameScene' });
  }

  /**
   * 接收启动参数：
   *   startFloor —— 从第几层开始（选关界面传入）。
   * 会被夹取到「1 .. 已通关的最高层」，未通关的层无法通过参数跳过。
   */
  init(data?: { startFloor?: number }) {
    this.startFloorOverride = clampStartFloor(data?.startFloor ?? 1);
  }

  create() {
    this.highScore = loadNumber(HIGH_SCORE_KEY);
    this.highFloor = loadNumber(HIGH_FLOOR_KEY);
    this.score = 0;
    this.floor = this.startFloorOverride;
    this.gameOver = false;
    this.deathTimer = 0;
    this.deathTransitioned = false;
    this.paused = false;
    this.doorUnlocked = false;

    // 1. 物理组（子弹 / 敌弹用对象池）
    this.bullets = this.physics.add.group({ classType: Bullet, maxSize: 400, defaultKey: 'bullet' });
    this.enemies = this.physics.add.group();
    this.enemyBullets = this.physics.add.group({ classType: EnemyBullet, maxSize: 300, defaultKey: 'bullet_enemy' });
    this.walls = this.physics.add.staticGroup();
    this.doors = this.physics.add.staticGroup();
    this.chests = this.physics.add.staticGroup();
    this.potions = this.physics.add.group();
    this.coins = this.physics.add.group();

    // 2. 地牢 + 玩家
    this.builder = new DungeonBuilder(this, {
      walls: this.walls, doors: this.doors, chests: this.chests, potions: this.potions,
    });
    this.builder.build(this.floor);

    const safe = this.builder.safePosition();
    this.player = new Player(this, safe.x, safe.y, 'player_idle');
    this.cameras.main.startFollow(this.player, true, 0.12, 0.12);

    // 3. 敌人调度器（含 Enemy 需要的 world 回调）
    this.director = new EnemyDirector(this, {
      enemies: this.enemies,
      enemyBullets: this.enemyBullets,
      potions: this.potions,
      player: this.player,
      builder: this.builder,
      getFloor: () => this.floor,
      getPlayerPosition: () => ({ x: this.player.x, y: this.player.y }),
    });

    // 4. UI 模块
    this.hud = new Hud(this);
    this.bossBar = new BossBar(this);
    this.toasts = new Toasts(this);
    this.talents = new TalentPanel(this, {
      getPlayer: () => this.player,
      getFloor: () => this.floor,
      pausePhysics: () => this.physics.pause(),
      resumePhysics: () => this.physics.resume(),
      isGamePaused: () => this.paused,
      onTalentsChanged: () => this.updateHud(),
    });
    this.pause = new PauseMenu(this, () => this.setPaused(false), () => this.returnToMenu());

    // 5. 碰撞 / 重叠
    this.physics.add.collider(this.player, this.walls);
    // 飞行单位穿墙：processCallback 返回 false → 跳过与墙壁的碰撞
    this.physics.add.collider(this.enemies, this.walls, undefined, (e: any) => !(e as Enemy).flying);

    this.physics.add.overlap(this.bullets, this.walls, (b) => {
      (b as Bullet).deactivate();
    });
    // 敌方子弹不允许穿墙：命中墙壁即回收（抛射/爆破弹则在该点炸开）
    this.physics.add.overlap(this.enemyBullets, this.walls, (b) => {
      this.director.hitWall(b as EnemyBullet);
    });
    this.physics.add.overlap(this.enemyBullets, this.player, (_p, b) => {
      this.director.hitPlayer(b as EnemyBullet);
    });
    this.physics.add.overlap(this.bullets, this.enemies, (b, e) => {
      this.onBulletHitEnemy(b as Bullet, e as Enemy);
    });
    this.physics.add.overlap(this.player, this.enemies, (_p, e) => {
      this.onEnemyTouchPlayer(e as Enemy);
    });
    this.physics.add.overlap(this.player, this.chests, (_p, c) => {
      this.onPlayerChest(c as Chest);
    });
    this.physics.add.overlap(this.player, this.potions, (_p, po) => {
      this.onPlayerPotion(po as Potion);
    });
    this.physics.add.overlap(this.player, this.coins, (_p, c) => {
      this.onPlayerCoin(c as Coin);
    });
    this.physics.add.overlap(this.player, this.doors, (_p, d) => {
      this.onPlayerDoor((d as Door).direction);
    });

    // 6. 输入
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = this.input.keyboard!.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      right: Phaser.Input.Keyboard.KeyCodes.D,
    });
    this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.bindKeys();

    // 7. 开始楼层（默认第 1 层；从选关进入则直接跳到所选层）
    this.startFloor();
    // 跳层进入时给一次开局天赋，弥补被跳过的成长（第 1 层开局仍旧不给）
    if (this.startFloorOverride > 1) this.talents.open();
  }

  // ── 输入绑定 ────────────────────────────────────────────────

  private bindKeys() {
    const kb = this.input.keyboard!;

    kb.on('keydown-ESC', () => {
      if (this.talents.isOpen) {
        this.talents.escape();
        return;
      }
      this.setPaused(!this.paused);
    });

    kb.on('keydown-F', () => {
      this.autoFire = !this.autoFire;
      this.hud.setAutoFire(this.autoFire);
    });

    kb.on('keydown-SPACE', () => this.tryFire(this.time.now));

    // 菜单键盘导航（上下左右 + 回车）——作用于当前打开的菜单
    const nav = (dx: number, dy: number) => {
      if (this.talents.isOpen) this.talents.move(dx, dy);
      else if (this.paused) this.pause.move(dx, dy);
    };
    kb.on('keydown-UP', () => nav(0, -1));
    kb.on('keydown-DOWN', () => nav(0, 1));
    kb.on('keydown-LEFT', () => nav(-1, 0));
    kb.on('keydown-RIGHT', () => nav(1, 0));
    kb.on('keydown-ENTER', () => {
      if (this.talents.isOpen) this.talents.select();
      else if (this.paused) this.pause.select();
    });
  }

  // ── 主循环 ──────────────────────────────────────────────────

  update(time: number, delta: number) {
    this.toasts.update(delta);

    if (this.gameOver) {
      this.deathTimer += delta;
      if (!this.deathTransitioned && this.deathTimer >= DEATH_TRANSITION_MS) {
        this.deathTransitioned = true;
        this.scene.start('GameOverScene', { score: this.score, floor: this.floor });
      }
      return;
    }
    if (this.paused || this.talents.isOpen) return;

    this.player.move(this.cursors, this.wasd, delta);
    this.player.updateShield(delta);
    this.autoAimAndFire(time);

    // 仅对相机视野内的敌人跑 AI（视野外休眠）
    const cam = this.cameras.main;
    const view = new Phaser.Geom.Rectangle(
      cam.worldView.x - VIEW_PAD_X,
      cam.worldView.y - VIEW_PAD_Y,
      cam.worldView.width + VIEW_PAD_X * 2,
      cam.worldView.height + VIEW_PAD_Y * 2
    );
    this.director.updateEnemies(time, delta, view);
    this.director.updateHoming(delta);

    this.magnetPickup();

    // 清空本层全部敌人 → 解锁下一层门
    if (!this.doorUnlocked && this.enemies.countActive(true) === 0) this.unlockDoor();

    if (this.player.hp <= 0) {
      this.triggerGameOver();
      return;
    }

    this.persistRecords();

    // BOSS 血条：有 BOSS 时显示并按当前血量刷新，否则隐藏（清空 = 击败）
    const boss = this.enemies
      .getChildren()
      .find((e: any) => (e as Enemy).active && (e as Enemy).isBoss) as Enemy | undefined;
    if (boss) this.bossBar.update(tr(boss.def.nameKey), boss.hp, boss.maxHp);
    else this.bossBar.hide();

    this.updateHud();
  }

  // ── 每层 ────────────────────────────────────────────────────

  private startFloor() {
    this.doorUnlocked = false;
    this.director.spawnWave(Bal.enemyCountForFloor(this.floor));
    if (this.floor % Bal.BOSS_EVERY === 0) this.announceBoss();
    if (this.player.regenPerFloor > 0) this.player.heal(this.player.regenPerFloor);
    this.builder.setDoorLocked(true);
    this.updateHud();
  }

  /** BOSS 登场提示（用怪物名，走 i18n） */
  private announceBoss() {
    const t = this.add
      .text(this.scale.width / 2, 118, tr('hud.bossWarning', { name: tr(BOSS_ENEMY.nameKey) }), {
        fontSize: '30px', fontFamily: 'monospace', color: UI.red,
        backgroundColor: '#000000aa', padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5).setScrollFactor(0).setDepth(DEPTH.bossBanner);
    this.tweens.add({ targets: t, alpha: 0, delay: 1500, duration: 600, onComplete: () => t.destroy() });
  }

  private updateHud() {
    this.hud.refresh({
      score: this.score,
      floor: this.floor,
      player: this.player,
      doorUnlocked: this.doorUnlocked,
      remainingEnemies: this.enemies.countActive(true),
      highScore: this.highScore,
      highFloor: this.highFloor,
    });
  }

  private unlockDoor() {
    this.doorUnlocked = true;
    this.builder.setDoorLocked(false);
    // 清空本层 = 通关本层 → 记入选关进度（单调递增）
    markFloorCleared(this.floor);
    this.toasts.push(this.player.x, this.player.y, tr('flash.killDone'), UI.green, { rise: 40, life: 1100 });
    if (this.builder.downDoor) {
      this.tweens.add({
        targets: this.builder.downDoor,
        alpha: { from: 1, to: 0.4 }, duration: 220, yoyo: true, repeat: 3,
      });
    }
  }

  private persistRecords() {
    if (this.score > this.highScore) {
      this.highScore = this.score;
      saveNumber(HIGH_SCORE_KEY, this.highScore);
    }
    if (this.floor > this.highFloor) {
      this.highFloor = this.floor;
      saveNumber(HIGH_FLOOR_KEY, this.highFloor);
    }
  }

  // ── 暂停 / 返回菜单 ─────────────────────────────────────────

  private setPaused(p: boolean) {
    this.paused = p;
    if (p) this.physics.pause();
    else this.physics.resume();
    this.pause.setVisible(p);
  }

  private returnToMenu() {
    this.setPaused(false);
    this.physics.resume();
    this.scene.start('MenuScene');
  }

  // ── 瞄准 / 射击 ─────────────────────────────────────────────

  private findNearestEnemy(): Enemy | null {
    let nearest: Enemy | null = null;
    let nearestDist = Infinity;
    this.enemies.getChildren().forEach((e: any) => {
      if (!e.active) return;
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, e.x, e.y);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = e as Enemy;
      }
    });
    return nearest;
  }

  /** 有敌人 → 自动瞄准最近敌人；无敌人 → 按玩家当前朝向 */
  private playerAimAngle(): number {
    const nearest = this.findNearestEnemy();
    if (nearest) {
      return Phaser.Math.Angle.Between(this.player.x, this.player.y, nearest.x, nearest.y);
    }
    return this.player.facingAngle;
  }

  /** 统一射击入口：受射速冷却限制。按键（含空场）与按住连射都走这里。 */
  private tryFire(time: number) {
    if (this.paused || this.talents.isOpen || this.gameOver) return;
    if (time < this.fireTimer) return;
    this.fireBullet(this.playerAimAngle());
    this.fireTimer = time + this.player.fireRate;
  }

  private autoAimAndFire(time: number) {
    if (this.autoFire || this.spaceKey.isDown) this.tryFire(time);
  }

  private fireBullet(angle: number) {
    const p = this.player;
    const count = 1 + p.extraBullets;
    const spread = Phaser.Math.DegToRad(Bal.PLAYER_BULLET_SPREAD_DEG);

    for (let i = 0; i < count; i++) {
      const a = angle + (i - (count - 1) / 2) * spread;
      const bullet = this.bullets.get(p.x, p.y, 'bullet') as Bullet | null;
      if (!bullet) continue; // 池已满
      const crit = Math.random() < p.critChance;
      const dmg = p.bulletDamage * (crit ? p.critMul : 1);
      bullet.launch(p.x, p.y, a, p.bulletSpeed, dmg, p.pierce, crit, 'bullet');

      const token = bullet.shotCount;
      this.time.delayedCall(Bal.PLAYER_BULLET_LIFETIME_MS, () => {
        if (bullet.active && bullet.shotCount === token) bullet.deactivate();
      });
    }
  }

  // ── 拾取（磁力 / 宝箱 / 药水 / 金币）────────────────────────

  private magnetPickup() {
    const r = this.player.pickupRadius;
    if (r <= 0) return;
    const r2 = r * r;
    const near = (o: any) => {
      const dx = this.player.x - o.x;
      const dy = this.player.y - o.y;
      return dx * dx + dy * dy <= r2;
    };
    this.potions.getChildren().forEach((po: any) => {
      if (po.active && !po.collected && near(po)) this.onPlayerPotion(po as Potion);
    });
    this.coins.getChildren().forEach((co: any) => {
      if (co.active && !co.collected && near(co)) this.onPlayerCoin(co as Coin);
    });
  }

  private spawnCoin(x: number, y: number): Coin {
    const c = new Coin(this, x, y, coinValue(this.floor));
    this.coins.add(c);
    return c;
  }

  private onPlayerChest(chest: Chest) {
    if ((chest as any).opened) return;
    chest.open();
    const gained = Math.round(CHEST_SCORE * this.player.scoreMul);
    this.score += gained;
    this.player.heal(CHEST_HEAL);
    this.toasts.push(this.player.x, this.player.y, tr('pickup.chest', { s: gained, h: CHEST_HEAL }), UI.gold);
    for (let i = 0; i < CHEST_COINS; i++) this.spawnCoin(chest.x, chest.y);
  }

  private onPlayerPotion(potion: Potion) {
    if (potion.collected || !potion.active) return;
    potion.collect();
    this.player.heal(POTION_HEAL);
    this.toasts.push(this.player.x, this.player.y, tr('pickup.potion', { h: POTION_HEAL }), UI.danger);
  }

  private onPlayerCoin(coin: Coin) {
    if (coin.collected || !coin.active) return;
    const gained = Math.round(coin.value * this.player.scoreMul);
    this.score += gained;
    this.toasts.push(this.player.x, this.player.y, tr('pickup.coin', { n: gained }), '#ffd94a');
    coin.collect();
  }

  // ── 战斗回调 ────────────────────────────────────────────────

  private onBulletHitEnemy(bullet: Bullet, enemy: Enemy) {
    if (!bullet.active || !enemy.active) return;

    const p = this.player;
    let dmg = bullet.damage;
    if ((enemy.isElite || enemy.isBoss) && p.eliteDmgMul > 1) dmg *= p.eliteDmgMul;

    enemy.takeDamage(dmg, p.slowAmount);
    if (p.healPerHit > 0) p.heal(p.healPerHit);
    if (enemy.hp <= 0) this.onEnemyKilled(enemy);

    if (bullet.pierce > 0) bullet.pierce -= 1;
    else bullet.deactivate();
  }

  private onEnemyKilled(enemy: Enemy) {
    const p = this.player;
    this.score += Math.round(KILL_BASE_SCORE * this.floor * scoreMultiplier(enemy) * p.scoreMul);
    if (p.lifesteal > 0) p.heal(p.lifesteal);

    this.director.dropPotionIfAny(enemy);
    const coins = coinDropCount(enemy);
    for (let i = 0; i < coins; i++) this.spawnCoin(enemy.x, enemy.y);

    enemy.destroy();
  }

  private onEnemyTouchPlayer(enemy: Enemy) {
    if (!enemy.active || this.gameOver) return;
    const p = this.player;

    // 荆棘护甲：撞击反伤
    if (p.thorns > 0) {
      enemy.takeDamage(p.thorns);
      if (enemy.hp <= 0) {
        this.onEnemyKilled(enemy);
        return;
      }
    }

    if (this.time.now < p.knockbackUntil) return;
    p.knockbackUntil = this.time.now + 220;
    const kb = 180 * (1 - Math.min(0.8, p.knockbackResist));
    const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, p.x, p.y);
    (p.body as Phaser.Physics.Arcade.Body).setVelocity(Math.cos(angle) * kb, Math.sin(angle) * kb);
  }

  // ── 死亡 / 换层 ─────────────────────────────────────────────

  private triggerGameOver() {
    if (this.gameOver) return;
    this.gameOver = true;
    this.deathTimer = 0;
    this.deathTransitioned = false;
    this.bossBar.hide();
    this.physics.pause();
    (this.player.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    this.player.setAlpha(1);
    this.player.setTint(TINT.dead);
    this.cameras.main.flash(140, 130, 0, 0);
    this.cameras.main.shake(240, 0.012);
  }

  private onPlayerDoor(direction: DoorDirection) {
    if (this.time.now < this.doorCooldown) return;

    // 两个门（上/下）都需要清空本层敌人才能使用
    if (!this.doorUnlocked) {
      this.toasts.push(
        this.player.x, this.player.y,
        tr('flash.clearFirst', { n: this.enemies.countActive(true) }),
        UI.orange, { rise: 40, life: 1100 }
      );
      return;
    }

    if (direction === 'up') {
      if (this.floor <= 1) return;
      this.floor--;
    } else {
      this.floor++;
      this.score += Math.round(FLOOR_CLEAR_SCORE * this.floor * this.player.scoreMul);
    }

    this.clearLevel();
    this.builder.build(this.floor);
    const safe = this.builder.safePosition();
    this.player.setPosition(safe.x, safe.y);
    (this.player.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);

    // 进出门：3 秒无敌护盾（护盾直径 = 角色 3 倍）
    this.player.grantInvincibility(Player.INVULN_MS);

    this.startFloor();
    // 进门 → 选择天赋
    this.talents.open();

    this.cameras.main.flash(120, 255, 255, 255);
    this.doorCooldown = this.time.now + DOOR_COOLDOWN_MS;
  }

  private clearLevel() {
    this.director.clear();
    this.coins.clear(true, true);
    this.potions.clear(true, true);
    this.chests.clear(true, true);
    this.walls.clear(true, true);
    this.doors.clear(true, true);
    this.bullets.getChildren().forEach((b) => (b as Bullet).deactivate());
  }
}
